/**
 * Dettaglio di un portafoglio: titoli, valori, andamento e date di acquisto.
 *
 * Le due fonti dicono cose diverse e la pagina lo rende esplicito: lo snapshot
 * dà il valore di **oggi**, i movimenti dicono quanto è costato e **da quando**
 * la posizione è aperta. Dove una delle due manca, la colonna resta vuota
 * invece di mostrare uno zero che sembrerebbe un dato.
 */

import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ApiError } from '@/lib/api-client';
import {
  INSTRUMENT_TYPE_LABELS,
  PORTFOLIO_TYPE_LABELS,
  formatCurrency,
  formatDate,
  formatDateTime,
  formatNumber,
  formatPercent,
  formatQuantity,
  labelFor,
  signOf,
} from '@/lib/format';
import { CostBasisModal } from './CostBasisModal';
import { CryptoTradeButton } from './CryptoTradeButton';
import { ManualOrderButton } from './ManualOrderButton';
import { TxTypeBadge } from './TxTypeBadge';
import { PortfolioSyncButton } from './PortfolioSyncButton';
import { useWishlist, type WishlistItem } from '@/features/wishlist/api';
import { WishlistItemModal } from '@/features/wishlist/WishlistItemModal';
import { WishlistTable } from '@/features/wishlist/WishlistTable';
import { Alert, Badge, Button, Card, DomainBadges, EmptyState, Spinner, StatCard, TableWrap } from '@/ui';
import { WatchlistToggleButton } from '@/features/watchlist/WatchlistToggleButton';
import {
  usePortfolio,
  usePortfolioHoldings,
  usePortfolioTransactions,
  type PortfolioTransaction,
  type Holding,
} from './api';
import { PortfolioValueTrend } from './PortfolioValueChart';
import { TransactionForm } from './TransactionForm';
import './portfolios.css';
import { InstrumentLogo } from '@/features/instruments/InstrumentLogo';
import {
  usePortfolioStrategyTags,
  useStrategyCatalog,
  type ActiveStrategyTag,
  type StrategyCatalogItem,
} from '@/features/strategy-tags/api';
import { StrategyChips } from '@/features/strategy-tags/StrategyChips';
import { StrategyTagsModal } from '@/features/strategy-tags/StrategyTagsModal';

const EMPTY = <span className="text-muted">—</span>;

function toneClass(value: string | null): string {
  const sign = signOf(value);
  return sign === 'positive' ? 'text-positive' : sign === 'negative' ? 'text-negative' : '';
}

/** Somma di stringhe decimali ai soli fini di riepilogo visivo. */
function sumOf(values: readonly (string | null)[]): string | null {
  const usable = values.filter((value): value is string => value !== null && value !== '');
  if (usable.length === 0) return null;
  return String(usable.reduce((total, value) => total + Number(value), 0));
}


/** Quanti movimenti mostrare nell'anteprima espansa: uno sguardo, non un elenco. */
const PREVIEW_LIMIT = 5;

/**
 * Vista ridotta dei movimenti di un titolo, aperta dalla riga della tabella.
 *
 * I dati sono caricati solo quando la riga viene espansa (`enabled`): una
 * richiesta per ogni titolo del portafoglio, fatta comunque, sarebbe sprecata
 * nella stragrande maggioranza dei casi.
 */
function HoldingMovementsPreview({
  portfolioId,
  holding,
}: {
  portfolioId: string;
  holding: Holding;
}) {
  const transactionsQuery = usePortfolioTransactions(portfolioId, {
    instrumentId: holding.instrument.id,
    limit: PREVIEW_LIMIT,
  });

  const total = transactionsQuery.data?.total ?? 0;
  const items = transactionsQuery.data?.items ?? [];

  return (
    <div className="holding-preview">
      {transactionsQuery.isPending ? (
        <Spinner label="Caricamento movimenti…" />
      ) : items.length === 0 ? (
        <p className="text-muted">Nessun movimento importato per questo titolo.</p>
      ) : (
        <>
          <ul className="holding-preview__list">
            {items.map((transaction) => (
              <li key={transaction.id} className="holding-preview__item">
                <span className="holding-preview__date">{formatDate(transaction.txDate)}</span>
                <TxTypeBadge txType={transaction.txType} />
                <span className="holding-preview__qty numeric">
                  {transaction.quantity ? formatQuantity(transaction.quantity) : '—'}
                </span>
                <span className={`holding-preview__amount numeric ${toneClass(transaction.amount)}`}>
                  {formatCurrency(transaction.amount, transaction.currency)}
                </span>
              </li>
            ))}
          </ul>
          {total > items.length && (
            <p className="holding-preview__more">
              Mostrati {formatNumber(items.length)} di {formatNumber(total)} movimenti.
            </p>
          )}
        </>
      )}

      <div className="holding-preview__actions">
        <Link
          className="btn btn--secondary"
          to={`/portfolios/${portfolioId}/instruments/${holding.instrument.id}`}
        >
          Dettaglio completo
        </Link>
        {/* Scheda del titolo di per sé (SPEC-016), fuori dal contesto del portafoglio. */}
        <Link className="btn btn--ghost" to={`/instruments/${holding.instrument.id}`}>
          Scheda titolo
        </Link>
        <WatchlistToggleButton
          instrumentId={holding.instrument.id}
          instrumentName={holding.instrument.name}
        />
      </div>
    </div>
  );
}

function HoldingRow({
  holding,
  portfolioId,
  expanded,
  onToggle,
  strategyTags,
  strategyCatalog,
}: {
  holding: Holding;
  portfolioId: string;
  expanded: boolean;
  onToggle: () => void;
  /** F-196: i tag di strategia attivi della posizione. */
  strategyTags: ActiveStrategyTag[];
  strategyCatalog: StrategyCatalogItem[] | undefined;
}) {
  const history = holding.history;
  const [editingStrategy, setEditingStrategy] = useState(false);

  return (
    <>
    <tr className={expanded ? 'holding-row--expanded' : undefined}>
      <td>
        <div className="portfolios__instrument">
          {/* Il titolo apre l'anteprima invece di navigare: uno sguardo ai
              movimenti senza perdere di vista il resto del portafoglio. */}
          <button
            type="button"
            className="holding-row__toggle"
            aria-expanded={expanded}
            onClick={onToggle}
          >
            <span aria-hidden="true" className="holding-row__chevron">
              {expanded ? '▾' : '▸'}
            </span>
            <InstrumentLogo instrumentId={holding.instrument.id} name={holding.instrument.name} />
            {holding.instrument.name}
          </button>
          <span className="portfolios__instrument-meta">
            {holding.instrument.isin ?? holding.instrument.symbol ?? '—'} ·{' '}
            {labelFor(INSTRUMENT_TYPE_LABELS, holding.instrument.instrumentType)}
            <button
              type="button"
              className="strategy-edit"
              onClick={() => setEditingStrategy(true)}
              title={`Tag di strategia di ${holding.instrument.name}`}
            >
              {strategyTags.length === 0 ? '+ strategia' : 'strategia'}
            </button>
          </span>
          <StrategyChips tags={strategyTags} catalog={strategyCatalog} />
          {editingStrategy && (
            <StrategyTagsModal
              portfolioId={portfolioId}
              instrumentId={holding.instrument.id}
              instrumentName={holding.instrument.name}
              onClose={() => setEditingStrategy(false)}
            />
          )}
        </div>
      </td>
      <td className="cell--minor col--tertiary">{history?.firstBuyDate ? formatDate(history.firstBuyDate) : EMPTY}</td>
      <td className="numeric">{formatQuantity(holding.quantity)}</td>
      <td className="numeric col--secondary">
        {holding.currentPrice ? formatCurrency(holding.currentPrice, holding.currency) : EMPTY}
      </td>
      <td className="numeric">
        {holding.marketValue ? (
          formatCurrency(holding.marketValue, holding.currency)
        ) : (
          <Badge tone="negative">da valorizzare</Badge>
        )}
      </td>
      <td className="numeric col--secondary">
        {history ? formatCurrency(history.investedEur, holding.currency) : EMPTY}
      </td>
      <td className={`numeric ${toneClass(holding.unrealizedPl)}`}>
        {holding.unrealizedPl === null ? (
          EMPTY
        ) : (
          <>
            {formatCurrency(holding.unrealizedPl, holding.currency)}
            <span className="portfolios__pct">{formatPercent(holding.unrealizedPlPct)}</span>
          </>
        )}
      </td>
      {/* Il peso e' una QUOTA, non una variazione: senza segno, o «+21,2 %» si
          legge come una plusvalenza. E in secondo piano, perche' e' contesto —
          la colonna che conta accanto e' il P&L. */}
      <td className="numeric cell--minor col--tertiary">
        {holding.weightPct ? formatPercent(holding.weightPct, 1, { signed: false }) : EMPTY}
      </td>
      <td className="numeric cell--minor col--tertiary">{history ? formatNumber(history.transactionsCount) : EMPTY}</td>
      <td>
        <WatchlistToggleButton
          instrumentId={holding.instrument.id}
          instrumentName={holding.instrument.name}
          compact
        />
      </td>
    </tr>
    {expanded && (
      <tr className="holding-row__panel">
        <td colSpan={10}>
          <HoldingMovementsPreview portfolioId={portfolioId} holding={holding} />
        </td>
      </tr>
    )}
    </>
  );
}


/**
 * "In wishlist" (SPEC-012.2 §4.3): i candidati all'acquisto per QUESTO
 * portafoglio. Chiamata separata e non un campo del DTO portafoglio — stesso
 * pattern già usato per lo storico movimenti per titolo (F-033): la sezione può
 * caricare, fallire o restare vuota senza trascinarsi dietro il resto della
 * pagina.
 */
function PortfolioWishlist({ portfolioId }: { portfolioId: string }) {
  const [editing, setEditing] = useState<WishlistItem | null>(null);
  const wishlist = useWishlist(portfolioId);
  const items = wishlist.data?.items ?? [];

  return (
    <Card
      title="In wishlist"
      subtitle="Titoli che hai segnato come candidati all'acquisto per questo portafoglio"
      flush={items.length > 0}
    >
      {wishlist.isLoading && <Spinner label="Caricamento dei candidati…" />}

      {wishlist.isError && (
        <Alert variant="error" title="Candidati non disponibili">
          {wishlist.error instanceof ApiError ? wishlist.error.message : 'Errore imprevisto.'}
        </Alert>
      )}

      {wishlist.data && items.length === 0 && (
        <EmptyState
          icon="🎯"
          title="Nessun candidato per questo portafoglio"
          description="Dalla watchlist, sulla riga di un titolo, «Wishlist» lo segna come candidato all'acquisto per un portafoglio, con esposizione massima e scadenza."
          action={
            <Link className="btn btn--secondary" to="/watchlists">
              Vai alla watchlist
            </Link>
          }
        />
      )}

      {items.length > 0 && (
        // La colonna "Portafoglio" sarebbe la stessa su ogni riga: qui è rumore.
        <WishlistTable items={items} showPortfolio={false} onEdit={setEditing} />
      )}

      {editing && (
        <WishlistItemModal
          item={editing}
          defaultExpiryDays={wishlist.data?.defaultExpiryDays ?? 7}
          onClose={() => setEditing(null)}
        />
      )}
    </Card>
  );
}


/**
 * Da dove viene un movimento (F-096).
 *
 * Tre casi distinti, e distinguerli e' il punto: un movimento generato dal
 * motore, uno registrato a mano a partire da un'analisi, e uno importato o
 * scritto dall'utente. Marcarli tutti allo stesso modo — o non marcarli
 * affatto, com'era prima — rende impossibile chiedersi «perche' ho questo
 * titolo?» e ottenere una risposta.
 *
 * Il badge «AI» compare **solo** dove c'e' davvero una decisione automatica
 * dietro: un deposito di apertura non e' un'operazione del motore, e dirlo
 * sarebbe falso.
 */
/**
 * Quanto ha reso una vendita (F-118).
 *
 * ## Perche' non basta l'importo che c'e' gia'
 *
 * La colonna «Importo» dice quanto e' **entrato in cassa**, che su una vendita
 * e' sempre un numero positivo: una vendita in perdita e una in guadagno si
 * assomigliano. Il risultato vero e' l'incasso meno il costo di carico di
 * quelle unita', e il costo di carico dipende da cosa e' stato comprato prima —
 * non e' leggibile dalla riga.
 *
 * Sotto la cifra sta il **raffronto** che la spiega: prezzo di vendita contro
 * prezzo medio di acquisto. Senza, il numero andrebbe creduto sulla parola.
 *
 * ⚠️ Quando il costo non e' noto — l'acquisto precede lo storico importato — il
 * realizzato coincide con l'incasso ed e' una **sovrastima**. Si dice, invece
 * di mostrare un guadagno che non e' stato tutto guadagno.
 */
function RealizedResult({ transaction }: { transaction: PortfolioTransaction }) {
  const sale = transaction.sale;
  if (!sale) return <span className="cell--minor">{EMPTY}</span>;

  if (sale.costUnknown) {
    return (
      <span
        className="cell--minor"
        title="L'acquisto precede lo storico importato: senza costo di carico il risultato coinciderebbe con l'incasso, cioè sarebbe sovrastimato."
      >
        costo ignoto
      </span>
    );
  }

  return (
    <span className={toneClass(sale.realized)}>
      {formatCurrency(sale.realized, transaction.currency)}
      <span className="portfolios__pct">
        {transaction.price ? `${formatCurrency(transaction.price, transaction.currency)} ` : ''}
        contro {formatCurrency(sale.averageCost, transaction.currency)}
      </span>
    </span>
  );
}

/**
 * Il valore di carico sotto l'importo di un trasferimento in entrata (F-174).
 *
 * L'importo di un trasferimento e' zero — non muove cassa — e da solo fa
 * pensare che la quantita' sia arrivata gratis. Sotto sta quanto pesa nel costo
 * medio, e da li' si corregge: e' l'unico movimento il cui costo il broker non
 * conosce.
 */
function TransferCostBasis({ transaction, onEdit }: { transaction: PortfolioTransaction; onEdit: () => void }) {
  const label =
    transaction.costBasis === null
      ? 'carico: nessuno'
      : `carico ${formatCurrency(transaction.costBasis, transaction.currency)}${transaction.costBasisSource === 'manual' ? ' (tuo)' : ''}`;
  return (
    <button
      type="button"
      className="portfolios__pct tx-cost-basis"
      onClick={onEdit}
      title={
        transaction.costBasisSource === 'market'
          ? 'Prezzo di mercato alla ricezione. Clicca per scrivere quanto l’avevi pagato.'
          : 'Clicca per impostare il valore di carico'
      }
    >
      {label}
    </button>
  );
}

function hasCostBasis(transaction: PortfolioTransaction): boolean {
  return (
    transaction.txType === 'transfer_in' &&
    transaction.instrument !== null &&
    transaction.quantity !== null &&
    !transaction.quantity.startsWith('-') &&
    transaction.quantity !== '0'
  );
}

function TransactionOrigin({ transaction }: { transaction: PortfolioTransaction }) {
  if (transaction.proposalId) {
    return (
      <Link to={`/auto-trading/proposals/${transaction.proposalId}`} className="tx-origin">
        <Badge tone="accent">AI</Badge>
        <span className="tx-origin__link">riconcilia</span>
      </Link>
    );
  }
  if (transaction.sourceAnalysisId && transaction.instrument) {
    return (
      <Link
        to={`/instruments/${transaction.instrument.id}/analyses/${transaction.sourceAnalysisId}`}
        className="tx-origin"
      >
        <Badge>da analisi</Badge>
      </Link>
    );
  }
  return <span className="cell--minor">{transaction.source === 'import' ? 'import' : 'manuale'}</span>;
}

export function PortfolioDetailPage() {
  const { id = '' } = useParams<{ id: string }>();
  // Una riga aperta per volta: due anteprime aperte insieme allungano la
  // tabella al punto da perdere di vista il confronto tra i titoli.
  const [expandedHoldingId, setExpandedHoldingId] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [costBasisFor, setCostBasisFor] = useState<PortfolioTransaction | null>(null);

  const portfolioQuery = usePortfolio(id);
  const holdingsQuery = usePortfolioHoldings(id);
  // F-196: i tag di strategia in una chiamata sola per tutte le righe.
  const strategyTags = usePortfolioStrategyTags(id);
  const strategyCatalog = useStrategyCatalog();
  const transactionsQuery = usePortfolioTransactions(id);

  if (portfolioQuery.isPending) return <Spinner label="Caricamento del portafoglio…" />;

  if (portfolioQuery.isError) {
    const error = portfolioQuery.error;
    const notFound = error instanceof ApiError && error.status === 404;

    return (
      <div className="portfolios">
        <Alert variant="error" title={notFound ? 'Portafoglio non trovato' : 'Errore di caricamento'}>
          {error instanceof ApiError ? error.message : 'Errore imprevisto.'}
        </Alert>
        <Link className="btn btn--secondary" to="/portfolios">
          Torna ai portafogli
        </Link>
      </div>
    );
  }

  const portfolio = portfolioQuery.data;
  const holdings = holdingsQuery.data?.items ?? [];
  const snapshot = portfolio.latestSnapshot;
  const currency = snapshot?.currency ?? portfolio.baseCurrency;

  const invested = sumOf(holdings.map((holding) => holding.history?.investedEur ?? null));
  const unrealized = sumOf(holdings.map((holding) => holding.unrealizedPl));
  const realized = sumOf(holdings.map((holding) => holding.history?.realizedPlEur ?? null));
  const withoutValue = holdings.filter((holding) => holding.marketValue === null).length;
  const withoutHistory = holdings.filter((holding) => holding.history === null).length;

  return (
    <div className="portfolios">
      <header className="portfolios__header">
        <div>
          <nav className="portfolios__breadcrumb">
            <Link to="/portfolios">Portafogli</Link> <span aria-hidden="true">›</span>{' '}
            <span>{portfolio.name}</span>
          </nav>
          <h1 className="portfolios__title">{portfolio.name}</h1>
          <p className="portfolios__subtitle">
            {portfolio.platform.name} ·{' '}
            {labelFor(PORTFOLIO_TYPE_LABELS, portfolio.portfolioType)}
            {snapshot && ` · aggiornato al ${formatDate(snapshot.snapshotDate)}`}{' '}
            <DomainBadges domains={portfolio.domains} />
          </p>
        </div>
        <div className="portfolios__header-actions">
          <ManualOrderButton portfolioId={portfolio.id} holdings={holdings} />
          <CryptoTradeButton portfolioId={portfolio.id} />
          <PortfolioSyncButton portfolioId={portfolio.id} />
          <Button variant="secondary" onClick={() => setShowForm((value) => !value)}>
            {showForm ? 'Chiudi' : 'Registra movimento'}
          </Button>
        </div>
      </header>

      {showForm && (
        <Card title="Registra un movimento" subtitle="Anche se non hai tutti i dati: puoi lasciare vuoti i campi opzionali">
          <TransactionForm
            portfolioId={id}
            defaultCurrency={portfolio.baseCurrency}
            onSuccess={() => setShowForm(false)}
            onCancel={() => setShowForm(false)}
          />
        </Card>
      )}

      {withoutValue > 0 && (
        <Alert variant="warning" title="Alcune posizioni sono senza valore di mercato">
          {withoutValue === 1
            ? 'Una posizione è registrata con la sua quantità ma senza valore.'
            : `${withoutValue} posizioni sono registrate con la loro quantità ma senza valore.`}{' '}
          Il valore totale qui sotto è quindi una sottostima.
        </Alert>
      )}

      <section className="portfolios__stats">
        <StatCard
          label="Valore attuale"
          value={snapshot ? formatCurrency(snapshot.totalValue, currency) : '—'}
          hint={snapshot ? `${formatNumber(snapshot.holdingsCount)} posizioni` : 'Nessuno snapshot'}
        />
        {/* Mostrata per OGNI portafoglio, non più solo per i paper wallet: la
            liquidità è metà del patrimonio ed è il denominatore delle
            percentuali di wishlist e dei limiti operativi. Nasconderla su un
            conto collegato al broker rendeva impossibile capire perché «il 5%
            del portafoglio» valesse 18 euro invece di 246. */}
        <StatCard
          label={portfolio.portfolioType === 'paper' ? 'Liquidità simulata' : 'Liquidità'}
          value={formatCurrency(portfolio.cashBalance, portfolio.baseCurrency)}
          hint={
            portfolio.portfolioType === 'paper'
              ? 'Saldo di apertura 10.000, aggiornato a ogni movimento'
              : 'Somma dei movimenti importati. Non è il saldo letto dal broker in tempo reale.'
          }
        />
        {/* Il patrimonio è ciò su cui si calcolano le percentuali: mostrarlo
            accanto ai suoi due addendi evita di doverlo ricostruire a mente. */}
        {portfolio.totalWithCash && (
          <StatCard
            label="Patrimonio"
            value={formatCurrency(portfolio.totalWithCash, currency)}
            hint="Titoli + liquidità: è la base delle percentuali di wishlist e dei limiti"
          />
        )}
        <StatCard
          label="Investito"
          value={invested ? formatCurrency(invested, currency) : '—'}
          hint={
            withoutHistory > 0
              ? `${withoutHistory} posizioni senza movimenti importati`
              : 'Costo medio ponderato'
          }
        />
        <StatCard
          label="P&L non realizzata"
          value={unrealized ? formatCurrency(unrealized, currency) : '—'}
          tone={
            signOf(unrealized) === 'positive'
              ? 'positive'
              : signOf(unrealized) === 'negative'
                ? 'negative'
                : 'neutral'
          }
          hint="Solo posizioni con valore e costo"
          // F-182: lo stato giorno per giorno, da questo numero in poi.
          action={
            <Link className="btn btn--secondary btn--sm" to={`/portfolios/${portfolio.id}/performance?metric=unrealized`}>
              Grafico del rendimento
            </Link>
          }
        />
        <StatCard
          label="P&L realizzata"
          value={realized ? formatCurrency(realized, currency) : '—'}
          tone={
            signOf(realized) === 'positive'
              ? 'positive'
              : signOf(realized) === 'negative'
                ? 'negative'
                : 'neutral'
          }
          hint="Da vendite già eseguite"
        />
      </section>

      <PortfolioValueTrend
        portfolioId={portfolio.id}
        portfolioName={portfolio.name}
        fallbackCurrency={currency}
      />

      <Card
        title="Titoli in portafoglio"
        subtitle={
          snapshot
            ? `Posizioni dello snapshot del ${formatDate(snapshot.snapshotDate)}, con costo e date dai movimenti`
            : 'Nessuno snapshot disponibile'
        }
        flush
      >
        {holdingsQuery.isPending ? (
          <Spinner />
        ) : holdings.length === 0 ? (
          <EmptyState
            icon="📄"
            title="Nessuna posizione"
            description="Importa un estratto del patrimonio per popolare i titoli di questo portafoglio."
          />
        ) : (
          <TableWrap tall>
            <table className="table table--compact table--sticky-head">
              <thead>
                <tr>
                  <th>Titolo</th>
                  <th className="col--tertiary">Primo acquisto</th>
                  <th className="numeric">Quantità</th>
                  <th className="numeric col--secondary">Prezzo</th>
                  <th className="numeric">Valore</th>
                  <th className="numeric col--secondary">Investito</th>
                  <th className="numeric">P&amp;L</th>
                  <th className="numeric col--tertiary">Peso</th>
                  <th className="numeric col--tertiary">Mov.</th>
                  <th><span className="visually-hidden">Watchlist</span></th>
                </tr>
              </thead>
              <tbody>
                {holdings.map((holding) => (
                  <HoldingRow
                    key={holding.id}
                    holding={holding}
                    portfolioId={id}
                    expanded={expandedHoldingId === holding.id}
                    strategyTags={strategyTags.data?.items?.[holding.instrument.id] ?? []}
                    strategyCatalog={strategyCatalog.data?.items}
                    onToggle={() =>
                      setExpandedHoldingId((current) =>
                        current === holding.id ? null : holding.id,
                      )
                    }
                  />
                ))}
              </tbody>
            </table>
          </TableWrap>
        )}
      </Card>

      <Card
        title="Movimenti"
        subtitle={
          transactionsQuery.data
            ? `Ultimi ${formatNumber(transactionsQuery.data.items.length)} di ${formatNumber(transactionsQuery.data.total)}`
            : undefined
        }
        flush
      >
        {transactionsQuery.isPending ? (
          <Spinner />
        ) : (transactionsQuery.data?.items.length ?? 0) === 0 ? (
          <EmptyState
            icon="📥"
            title="Nessun movimento"
            description="Importa la cronologia operazioni per vedere acquisti, vendite e dividendi."
          />
        ) : (
          <TableWrap>
            <table className="table table--compact">
              <thead>
                <tr>
                  <th>Data</th>
                  <th>Tipo</th>
                  <th>Titolo</th>
                  <th className="numeric col--secondary">Quantità</th>
                  <th className="numeric col--tertiary">Prezzo</th>
                  <th className="numeric">Importo</th>
                  <th className="numeric">Realizzato</th>
                  <th>Origine</th>
                </tr>
              </thead>
              <tbody>
                {transactionsQuery.data?.items.map((transaction) => (
                  <tr key={transaction.id}>
                    <td className="cell--minor">{formatDateTime(transaction.txDate)}</td>
                    <td>
                      <TxTypeBadge txType={transaction.txType} />
                    </td>
                    <td>{transaction.instrument?.name ?? EMPTY}</td>
                    <td className="numeric col--secondary">
                      {transaction.quantity ? formatQuantity(transaction.quantity) : EMPTY}
                    </td>
                    <td className="numeric col--tertiary">
                      {transaction.price
                        ? formatCurrency(transaction.price, transaction.currency)
                        : EMPTY}
                    </td>
                    <td className={`numeric ${toneClass(transaction.amount)}`}>
                      {formatCurrency(transaction.amount, transaction.currency)}
                      {hasCostBasis(transaction) && (
                        <TransferCostBasis transaction={transaction} onEdit={() => setCostBasisFor(transaction)} />
                      )}
                    </td>
                    <td className="numeric">
                      <RealizedResult transaction={transaction} />
                    </td>
                    <td>
                      <TransactionOrigin transaction={transaction} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableWrap>
        )}
      </Card>

      {costBasisFor && (
        <CostBasisModal portfolioId={id} transaction={costBasisFor} onClose={() => setCostBasisFor(null)} />
      )}

      <PortfolioWishlist portfolioId={id} />
    </div>
  );
}
